import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { useAuth } from '../../../hooks/useAuth';
import { useUserSave } from '../../../hooks/useUserSave';
import { analyticsService } from '../../../services/analyticsService';
import type {
  LibraryBlockedTermsSyncResult,
  LibraryBlockedTermsSyncStatus,
} from './useLibraryModBlockedTermsSync';
import { useLibraryBlockedTermsSync } from './useLibraryModBlockedTermsSync';
import type { LibraryDialog } from '../libraryRouteTypes';
import { toLibraryModSettingsState } from '../moderation/libraryModSettingsResolver';
import type { ModSettings, UserAuthFields } from '@airhead-games/shared/types';

export type CensorshipViewModel = {
  censorCommonChecked: boolean;
  censorCustomChecked: boolean;
  censorRigidChecked: boolean;
  showRigidRow: boolean;
  showCustomWordsRow: boolean;
  showBlockedTwitchTermsRow: boolean;
  customWordsBadgeLabel: string;
  blockedTwitchTermsBadgeLabel: string | null;
  syncIssueOpen: boolean;
  onCensorCommonToggle: (checked: boolean) => void;
  onCensorCustomToggle: (checked: boolean) => void;
  onCensorRigidToggle: (checked: boolean) => void;
  onCustomWordsRowClick: () => void;
  onBlockedTwitchTermsRowClick: () => void;
  onSyncIssueOpenChange: (open: boolean) => void;
  onRetrySync: () => Promise<LibraryBlockedTermsSyncResult>;
  onReconnectTwitch: () => void;
};

type UseLibraryModCensorshipParams = {
  user: UserAuthFields | null;
  dialog: LibraryDialog | null;
  openDialog: (dialog: LibraryDialog) => void;
  requireAuth: () => void;
};

export type LibraryModCensorshipController = {
  modSettings: ModSettings;
  setModSettings: Dispatch<SetStateAction<ModSettings>>;
  markDirty: () => void;
  requireAuth: () => void;
  syncStatus: LibraryBlockedTermsSyncStatus;
  hasTwitchAuth: boolean;
  handleModScopeDenied: () => void;
  handleTwitchTermsBlockedToggle: (checked: boolean) => void;
  viewModel: CensorshipViewModel;
};

export function useLibraryModCensorship({
  user,
  dialog,
  openDialog,
  requireAuth,
}: UseLibraryModCensorshipParams): LibraryModCensorshipController {
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();
  const [modSettings, setModSettings] = useState<ModSettings>(() =>
    toLibraryModSettingsState(user)
  );
  const [syncIssueOpen, setSyncIssueOpen] = useState(false);
  const hasTwitchAuth = !!user?.authTwitch?.userId;
  const hasModScopeGrant = searchParams.get('modScopeGranted') === 'true';
  const { markDirty } = useUserSave({
    value: modSettings,
    setValue: setModSettings,
    toUpdate: (value) => ({
      modSettings: {
        censorCommon: value.censorCommon,
        censorCustom: value.censorCustom,
        censorRigid: value.censorRigid,
        censorCustomWords: value.censorCustomWords,
        twitchTermsBlocked: value.twitchTermsBlocked,
      },
    }),
    fromUser: toLibraryModSettingsState,
    errorMessage: 'Unable to save your censorship settings.',
  });

  const { syncStatus, handleSyncBlockedTerms, resetBlockedTermsSyncStatus } =
    useLibraryBlockedTermsSync({
      user,
      hasModScopeGrant,
      dialog,
      setUser,
      openDialog,
    });

  useEffect(() => {
    if (syncStatus !== 'error') {
      setSyncIssueOpen(false);
    }
  }, [syncStatus]);

  const promptConnectTwitch = useCallback(() => {
    toast.error('Link your Twitch account in Connections to use this feature.');
    openDialog('connections');
  }, [openDialog]);

  const updateBooleanSetting = useCallback(
    (
      key: 'censorCommon' | 'censorCustom' | 'censorRigid',
      checked: boolean,
      linkLocation: 'library_censorship'
    ) => {
      if (!user) {
        requireAuth();
        return;
      }

      analyticsService.trackPreferenceChanged({
        preference_type: key,
        preference_value: checked,
        link_location: linkLocation,
      });

      setModSettings((current) => ({
        ...current,
        [key]: checked,
        ...(key === 'censorCustom' && !checked
          ? { twitchTermsBlocked: false }
          : {}),
      }));
      markDirty();
    },
    [markDirty, requireAuth, setModSettings, user]
  );

  const handleTwitchTermsBlockedToggle = useCallback(
    (checked: boolean) => {
      if (!user) {
        requireAuth();
        return;
      }

      if (!modSettings.censorCustom) {
        return;
      }

      if (checked && !hasTwitchAuth) {
        promptConnectTwitch();
        return;
      }

      analyticsService.trackPreferenceChanged({
        preference_type: 'twitchTermsBlocked',
        preference_value: checked,
        link_location: 'library_censorship',
      });

      setModSettings((current) => ({
        ...current,
        twitchTermsBlocked: checked,
      }));

      if (checked) {
        void handleSyncBlockedTerms().then((result) => {
          if (result !== 'error') {
            return;
          }

          setModSettings((current) => ({
            ...current,
            twitchTermsBlocked: false,
          }));
        });
        return;
      }

      resetBlockedTermsSyncStatus();
      markDirty();
    },
    [
      handleSyncBlockedTerms,
      hasTwitchAuth,
      markDirty,
      modSettings.censorCustom,
      promptConnectTwitch,
      requireAuth,
      resetBlockedTermsSyncStatus,
      setModSettings,
      user,
    ]
  );

  const handleBlockedTwitchTermsRowClick = useCallback(() => {
    if (syncStatus === 'error') {
      setSyncIssueOpen(true);
      return;
    }

    openDialog('blocked-twitch-terms');
  }, [openDialog, syncStatus]);

  const handleRetrySync = useCallback(async () => {
    const result = await handleSyncBlockedTerms();
    if (result === 'synced') {
      setSyncIssueOpen(false);
      openDialog('blocked-twitch-terms');
    }
    return result;
  }, [handleSyncBlockedTerms, openDialog]);

  const handleReconnectTwitch = useCallback(() => {
    setSyncIssueOpen(false);
    openDialog('connections');
  }, [openDialog]);

  const handleModScopeDenied = useCallback(() => {
    setModSettings((current) => ({
      ...current,
      twitchTermsBlocked: false,
    }));
    resetBlockedTermsSyncStatus();
    markDirty();
  }, [markDirty, resetBlockedTermsSyncStatus, setModSettings]);

  const blockedTwitchTermsCount = modSettings.censorTwitchWords.length;
  const blockedTwitchTermsBadgeLabel = !modSettings.twitchTermsBlocked
    ? null
    : syncStatus === 'syncing'
      ? 'Syncing…'
      : syncStatus === 'error'
        ? 'Unavailable'
        : `Synced · ${blockedTwitchTermsCount}`;

  const viewModel = useMemo<CensorshipViewModel>(
    () => ({
      censorCommonChecked: modSettings.censorCommon,
      censorCustomChecked: modSettings.censorCustom,
      censorRigidChecked: modSettings.censorRigid,
      showRigidRow: modSettings.censorCommon || modSettings.censorCustom,
      showCustomWordsRow: modSettings.censorCustom,
      showBlockedTwitchTermsRow: modSettings.censorCustom,
      customWordsBadgeLabel: `${modSettings.censorCustomWords.length}`,
      blockedTwitchTermsBadgeLabel,
      syncIssueOpen,
      onCensorCommonToggle: (checked) =>
        updateBooleanSetting('censorCommon', checked, 'library_censorship'),
      onCensorCustomToggle: (checked) =>
        updateBooleanSetting('censorCustom', checked, 'library_censorship'),
      onCensorRigidToggle: (checked) =>
        updateBooleanSetting('censorRigid', checked, 'library_censorship'),
      onCustomWordsRowClick: () => openDialog('custom-words'),
      onBlockedTwitchTermsRowClick: handleBlockedTwitchTermsRowClick,
      onSyncIssueOpenChange: setSyncIssueOpen,
      onRetrySync: handleRetrySync,
      onReconnectTwitch: handleReconnectTwitch,
    }),
    [
      blockedTwitchTermsBadgeLabel,
      handleBlockedTwitchTermsRowClick,
      handleReconnectTwitch,
      handleRetrySync,
      modSettings.censorCommon,
      modSettings.censorCustom,
      modSettings.censorCustomWords.length,
      modSettings.censorRigid,
      openDialog,
      syncIssueOpen,
      updateBooleanSetting,
    ]
  );

  return useMemo(
    () => ({
      modSettings,
      setModSettings,
      markDirty,
      requireAuth,
      syncStatus,
      hasTwitchAuth,
      handleModScopeDenied,
      handleTwitchTermsBlockedToggle,
      viewModel,
    }),
    [
      handleModScopeDenied,
      handleTwitchTermsBlockedToggle,
      hasTwitchAuth,
      markDirty,
      modSettings,
      requireAuth,
      syncStatus,
      viewModel,
    ]
  );
}
